import { createSelector, Selector } from '@reduxjs/toolkit'
import { isNil } from 'lodash-es'
import { EntityType, HolidayDetail, Holiday } from 'api'
import { GlobalState } from 'store'
import { createAllEntityUpdatesSelector, createAllEntitiesSelector } from './entitySelectors'
import { DateLike, ParametricSelector } from 'utils'

export const holidayDetailsSelector: Selector<GlobalState, HolidayDetail[]> = createSelector(
  createAllEntityUpdatesSelector(EntityType.HOLIDAY),
  holidayUpdates => {
    return holidayUpdates.reduce((holidayDetails, holidayUpdate) => {
      const holidayEntity = holidayUpdate.entity
      const partialHolidayDetail: HolidayDetail[] = holidayEntity.holidays.map(date => ({
        holiday: holidayEntity,
        date,
        availableEvents: holidayUpdate.availableEvents
      }))

      return [...holidayDetails, ...partialHolidayDetail]
    }, [])
  }
)

export const createHolidayGetterSelector: Selector<GlobalState, (currency: string) => Holiday | null> = createSelector(
  createAllEntitiesSelector(EntityType.HOLIDAY),
  holidays => (currency: string) => {
    return holidays.find(holiday => holiday.currency === currency) || null
  }
)

export function createHolidayDatesSelector<TProps>(
  currencySelector: ParametricSelector<{}, TProps, string | null | undefined>
): ParametricSelector<GlobalState, TProps, DateLike[]> {
  return createSelector([createHolidayGetterSelector, currencySelector], (getHoliday, currency) => {
    if (isNil(currency)) {
      return []
    } else {
      const holiday = getHoliday(currency)
      return holiday ? holiday.holidays : []
    }
  })
}

export const createAllHolidayDatesSelector: Selector<GlobalState, DateLike[]> = createSelector(
  createAllEntitiesSelector(EntityType.HOLIDAY),
  holidays => holidays.map(holiday => holiday.holidays).flat()
)
